import axios from 'axios';

import configuration from '@totem/utilities/configuration';
import { TOKEN_HEARTBEAT_ENDPOINT } from '@totem/utilities/endpoints';
import { setPostLoginRedirect } from '@totem/utilities/loginRedirect';

/*
 * Sends the user to /login when their session has expired.
 *
 * Every API response goes through a fetch wrapper / axios interceptor. A 401
 * from the API is only a *hint*: some endpoints have historically used 401 for
 * permission failures, so before redirecting we confirm with the token
 * heartbeat (behind the API's auth middleware, so it returns 401 only for a
 * missing, invalid or expired token). Anything other than a 401 from the
 * heartbeat - 200, 5xx, network error - leaves the user where they are.
 */

// Pages that work without a signed-in session
const PUBLIC_PATH_PREFIXES = [
  '/login',
  '/logout',
  '/auth/callback',
  '/no-access',
  '/ticket/',
  '/ticket-action/',
  '/survey/',
  '/remoteAccessServices',
];

// The Remote Access password-reset portal is public on these hostnames
const PUBLIC_HOSTNAME_PREFIXES = [
  'ras',
  'd-ras',
  'passwordreset',
  'd-passwordreset',
];

const INSTALLED_FLAG = '__totemSessionExpiryInstalled';

let originalFetch: typeof window.fetch = null;
let confirming = false;
let redirecting = false;

const isPublicPage = () =>
  PUBLIC_HOSTNAME_PREFIXES.some((prefix) =>
    window.location.hostname.startsWith(prefix),
  ) ||
  PUBLIC_PATH_PREFIXES.some((prefix) =>
    window.location.pathname.startsWith(prefix),
  );

const apiOrigin = () => {
  try {
    return new URL(configuration.API_DOMAIN, window.location.href).origin;
  } catch {
    return null;
  }
};

const isApiRequest = (url: string) => {
  try {
    return new URL(url, window.location.href).origin === apiOrigin();
  } catch {
    return false;
  }
};

const getRequestUrl = (input: RequestInfo | URL) => {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  return input.url;
};

const getAuthorization = (input: RequestInfo | URL, init?: RequestInit) => {
  const headers = new Headers(
    input instanceof Request ? input.headers : undefined,
  );
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  return headers.get('Authorization');
};

const isSessionExpired = async (authorization: string) => {
  try {
    const response = await originalFetch(TOKEN_HEARTBEAT_ENDPOINT, {
      method: 'GET',
      headers: new Headers({ Authorization: authorization }),
    });
    return response.status === 401;
  } catch {
    return false;
  }
};

const redirectToLogin = () => {
  redirecting = true;

  // Return the user to this page after they sign in again (see AuthCallback)
  setPostLoginRedirect(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );

  // A full navigation also drops the stale in-memory session state
  window.location.assign('/login');
};

export const handleUnauthorized = (authorization: string | null) => {
  // Without the request's token there is nothing to confirm against
  if (!authorization || redirecting || confirming || isPublicPage()) {
    return;
  }

  confirming = true;

  isSessionExpired(authorization)
    .then((expired) => {
      if (expired) {
        redirectToLogin();
      }
    })
    .finally(() => {
      confirming = false;
    });
};

export const installSessionExpiryHandling = () => {
  // Guard against double-wrapping (e.g. hot module reloads)
  if (window[INSTALLED_FLAG]) {
    return;
  }
  window[INSTALLED_FLAG] = true;

  originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await originalFetch(input, init);
    if (response.status === 401 && isApiRequest(getRequestUrl(input))) {
      handleUnauthorized(getAuthorization(input, init));
    }
    return response;
  };

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 401 &&
        error.config &&
        isApiRequest(axios.getUri(error.config))
      ) {
        const authorization = error.config.headers?.Authorization;
        handleUnauthorized(
          typeof authorization === 'string' ? authorization : null,
        );
      }
      return Promise.reject(error);
    },
  );
};
